import { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Reusable destructive confirm dialog. The caller passes:
 * - open / onOpenChange
 * - title (default "Eliminar")
 * - description: jsx/string with the question
 * - warnings: optional array of strings shown in a yellow callout (e.g. open advances)
 * - confirmLabel (default "Eliminar"), busyLabel (default "Eliminando…")
 * - onConfirm: async function. The dialog handles the busy state.
 */
export default function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title = "Eliminar",
  description,
  warnings = [],
  confirmLabel = "Eliminar",
  busyLabel = "Eliminando...",
  onConfirm,
  testId,
}) {
  const [busy, setBusy] = useState(false);

  // Reset busy state when the dialog (re)opens.
  useEffect(() => {
    if (open) setBusy(false);
  }, [open]);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testId || "confirm-delete-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
            <span className="inline-flex w-8 h-8 rounded-full bg-rose-100 text-[#B91C1C] items-center justify-center">
              <AlertTriangle className="w-4 h-4" />
            </span>
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm text-[#334155] leading-relaxed">
          <p data-testid="confirm-description">{description}</p>

          {warnings.length > 0 && (
            <div
              data-testid="confirm-warnings"
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-1"
            >
              <div className="font-semibold text-amber-900">Advertencia</div>
              {warnings.map((w, i) => (
                <div key={i} className="flex gap-2">
                  <span aria-hidden>•</span>
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-[#64748B]">
            Esta acción no se puede deshacer.
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            data-testid="confirm-cancel-btn"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            data-testid="confirm-confirm-btn"
            onClick={handleConfirm}
            disabled={busy}
            className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
          >
            {busy ? busyLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
